import { useState } from 'react';
import { Phone, Calculator, ChevronDown, ChevronUp } from 'lucide-react';
import { COMPANY_INFO, AI_SATEI_URL } from '@/constants/company';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

const faqCategories = [
  {
    id: 'general',
    name: '一般的なご質問',
    faqs: [
      {
        question: '査定は本当に無料ですか？',
        answer: 'はい、AI査定も、後続の詳細査定も完全無料です。費用は一切かかりませんので、お気軽にご利用ください。査定結果にご納得いただけない場合は、ご契約いただく必要もありません。',
      },
      {
        question: '全国どこからでも相談できますか？',
        answer: 'はい、全国どこからでもご相談いただけます。ただし、名古屋16区（千種区・東区・北区・西区・中村区・中区・昭和区・瑞穂区・熱田区・中川区・港区・南区・守山区・緑区・名東区・天白区）の物件については、特に詳細な市場情報に基づいた査定が可能です。',
      },
      {
        question: '秘密厳守は本当ですか？',
        answer: 'はい、お客様の個人情報および物件情報は厳重に管理し、第三者への漏洩は絶対にいたしません。ご近所への声がけも行わず、内緒で売却を進めたい方にも安心してご利用いただけます。',
      },
    ],
  },
  {
    id: 'selling',
    name: '売却について',
    faqs: [
      {
        question: '売却までどのくらいの期間がかかりますか？',
        answer: '買取の場合、査定後最短7日〜14日で現金化が可能です。仲介の場合、市場状況によりますが、平均1〜3ヶ月程度を目安にしてください。急ぎの場合は、スピード買取をおすすめします。',
      },
      {
        question: 'ローンが残っている物件でも売却できますか？',
        answer: 'はい、売却可能です。残債と売却価格のバランスにより、任意売却や残債を持ち越した上での売却など、最適な方法をご提案いたします。まずは無料相談で状況をお聞かせください。',
      },
      {
        question: '相続した不動産を売りたいのですが、手続きが複雑で...',
        answer: '相続不動産の売却は、相続人の調整や登記手続きなど、専門的な知識が必要です。当社は相続に関する豊富な実績があり、税理士や司法書士との連携も可能です。煩雑な手続きもサポートいたします。',
      },
      {
        question: '空き家でも売却できますか？',
        answer: 'はい、空き家でも買取可能です。管理が行き届いていない物件や、古い物件でも、適正な価格で査定いたします。空き家は放置すると固定資産税が増額される場合もあるため、お早めにご相談ください。',
      },
    ],
  },
  {
    id: 'purchase',
    name: '買取について',
    faqs: [
      {
        question: '買取と仲介の違いは何ですか？',
        answer: '買取は、当社が直接物件を購入する方法です。スピーディーに現金化でき、内覧対応や掃除の必要がありません。仲介は、買主を探して売却する方法で、高値で売れる可能性がありますが、時間がかかる場合があります。',
      },
      {
        question: '買取価格は市場価格より安くなりますか？',
        answer: '買取は即金性や手間の軽減を考慮した価格となりますが、名古屋16区の豊富な実績とネットワークを活かし、できるだけ高値での買取を心がけています。具体的な価格は無料査定でご確認ください。',
      },
      {
        question: '築年数が古い物件でも買取してもらえますか？',
        answer: 'はい、築年数に関わらず査定いたします。リフォーム後に販売する物件や、土地としての価値を見込んで買取するケースもあります。まずは無料で査定させてください。',
      },
    ],
  },
  {
    id: 'process',
    name: '査定・契約について',
    faqs: [
      {
        question: 'AI査定の精度はどの程度ですか？',
        answer: 'AI査定は、過去の成約事例や市場データに基づいた目安の価格です。正確な価格を知りたい場合は、後続の詳細査定（無料）をご利用ください。実際の現地調査を経て、より正確な価格をご提示します。',
      },
      {
        question: '査定後、すぐに売却を決めなければなりませんか？',
        answer: 'いいえ、査定後はご検討期間を設けていただけます。他社との比較や、ご家族とのご相談も大歓迎です。ご納得いただいてからのご契約となりますので、焦る必要はありません。',
      },
      {
        question: '契約後にキャンセルできますか？',
        answer: '契約締結後のキャンセルは、一般的に違約金が発生する場合があります。ただし、契約前であればいつでもキャンセル可能です。契約内容について不明な点があれば、担当者が丁寧にご説明いたします。',
      },
    ],
  },
  {
    id: 'after',
    name: '売却後について',
    faqs: [
      {
        question: '引っ越しの期間はどのくらいもらえますか？',
        answer: '買取の場合、通常は1〜2ヶ月程度の猶予期間を設けることができます。お客様の状況に応じて調整可能ですので、ご相談ください。',
      },
      {
        question: '売却後の税金はかかりますか？',
        answer: '譲渡所得に対して税金がかかる場合があります。所有期間や居住状況により、譲渡所得控除や特別控除が適用される場合もあります。詳細は税理士にご相談いただくか、当社でも概要をご説明できます。',
      },
    ],
  },
];

export default function FaqPage() {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  const toggleItem = (categoryId: string, faqIndex: number) => {
    const key = `${categoryId}-${faqIndex}`;
    setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const faqSchemaItems = faqCategories.flatMap((cat) =>
    cat.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    }))
  );

  return (
    <>
      <title>よくある質問（FAQ）｜名古屋不動産売却・買取 クリアコーポレーション</title>
      <meta name="description" content="名古屋の不動産売却・買取に関するよくある質問。査定・売却期間・ローン残債・相続・空き家・キャンセル・対応エリアなど15問以上を掲載。株式会社クリアコーポレーション。" />
      <meta name="keywords" content="名古屋 不動産売却 よくある質問, 名古屋 不動産買取 FAQ, 不動産査定 質問, 名古屋 相続不動産 売却" />
      <meta property="og:title" content="よくある質問（FAQ）｜名古屋不動産売却・買取 クリアコーポレーション" />
      <meta property="og:description" content="名古屋の不動産売却・買取に関するよくある質問。査定・売却期間・ローン残債・相続・空き家など15問以上を掲載。" />
      <meta property="og:type" content="website" />
      <link rel="canonical" href="https://clearcorp.info/faq" />
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqSchemaItems,
      })}</script>
      <Header />
      <main className="min-h-screen bg-slate-50">
        {/* Hero Section */}
        <section className="bg-slate-900 py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              よくある質問
            </h1>
            <p className="text-slate-300 text-lg">
              お客様からよくいただく質問をまとめました。
              <br />
              その他のご質問は、お気軽にお問い合わせください。
            </p>
          </div>
        </section>

        {/* FAQ Content */}
        <section className="py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            {faqCategories.map((category) => (
              <div key={category.id} className="mb-12 last:mb-0">
                <h2 className="text-xl font-bold text-slate-900 mb-6 pb-2 border-b-2 border-slate-200">
                  {category.name}
                </h2>
                <div className="space-y-4">
                  {category.faqs.map((faq, index) => {
                    const key = `${category.id}-${index}`;
                    const isOpen = openItems[key];
                    return (
                      <div
                        key={index}
                        className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                      >
                        <button
                          onClick={() => toggleItem(category.id, index)}
                          className="w-full flex items-start justify-between p-5 md:p-6 text-left hover:bg-slate-50 transition-colors"
                        >
                          <span className="font-semibold text-slate-900 pr-4">
                            Q. {faq.question}
                          </span>
                          {isOpen ? (
                            <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                          )}
                        </button>
                        {isOpen && (
                          <div className="px-5 md:px-6 pb-5 md:pb-6">
                            <div className="pt-4 border-t border-slate-100">
                              <p className="text-slate-600 leading-relaxed">
                                A. {faq.answer}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              その他のご質問がございますか？
            </h2>
            <p className="text-slate-600 mb-8">
              お電話またはお問い合わせフォームから、お気軽にご相談ください。
              <br />
              専門スタッフが丁寧にお答えいたします。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`tel:${COMPANY_INFO.phone}`}
                className="inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold px-8 py-4 rounded-lg transition-colors"
              >
                <Phone className="w-5 h-5" />
                {COMPANY_INFO.phone}
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center gap-2 border-2 border-slate-900 text-slate-900 hover:bg-slate-50 font-semibold px-8 py-4 rounded-lg transition-colors"
              >
                お問い合わせフォーム
              </a>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
